import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { canonicalLink } from '@/lib/seo'

export const Route = createFileRoute('/unsubscribe')({
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  head: () => ({
    meta: [
      { title: 'Unsubscribe — BLVDS Detailing' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
    links: [canonicalLink('/unsubscribe')],
  }),
  component: UnsubscribePage,
})

function UnsubscribePage() {
  const { token } = Route.useSearch()
  const [state, setState] = useState<'loading' | 'valid' | 'already' | 'invalid' | 'success' | 'error'>('loading')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setState('invalid')
      return
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.valid) setState('valid')
        else if (d.reason === 'already_unsubscribed') setState('already')
        else setState('invalid')
      })
      .catch(() => setState('error'))
  }, [token])

  async function confirm() {
    if (!token) return
    setSubmitting(true)
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const d = await r.json()
      if (d.success) setState('success')
      else if (d.reason === 'already_unsubscribed') setState('already')
      else setState('error')
    } catch {
      setState('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Layout>
      <section className="min-h-[60vh] flex items-center max-w-xl mx-auto px-6 py-20 text-center">
        <div className="w-full">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Unsubscribe</h1>
          {state === 'loading' && <p className="text-muted-foreground">Checking your link…</p>}
          {state === 'invalid' && <p className="text-muted-foreground">This unsubscribe link is invalid or expired.</p>}
          {state === 'already' && <p className="text-muted-foreground">You've already been unsubscribed.</p>}
          {state === 'error' && <p className="text-destructive">Something went wrong. Please try again.</p>}
          {state === 'valid' && (
            <>
              <p className="text-muted-foreground mb-6">Click below to confirm you'd like to stop receiving emails from us.</p>
              <button
                onClick={confirm}
                disabled={submitting}
                className="px-6 py-3 rounded-xl bg-gradient-primary text-primary-foreground font-semibold disabled:opacity-60"
              >
                {submitting ? 'Unsubscribing…' : 'Confirm unsubscribe'}
              </button>
            </>
          )}
          {state === 'success' && <p className="text-foreground">You've been unsubscribed. We're sorry to see you go.</p>}
        </div>
      </section>
    </Layout>
  )
}
